/**
 * Cross-script product search.
 *
 * A product has an English name and a Nepali one, but the shopkeeper has one
 * keyboard. Matching the query as typed means someone with an English keyboard
 * can never reach a product whose only real name is Nepali: type "chini" and
 * चिनी does not come back, even though it is sitting on the shelf.
 *
 * So both sides are reduced to a phonetic key and compared there. The key is
 * built by transliterating Devanagari to Latin and then normalising the
 * spellings that Nepali romanisation leaves free ("chini"/"cheeni",
 * "sabun"/"saabun"), so the two scripts land on the same string.
 *
 * Matching is deliberately layered, cheapest and most exact first, so that a
 * literal match can never be lost to a phonetic near-miss.
 */

// Devanagari consonants, mapped onto the Latin the shopkeeper would type.
// Retroflex and dental collapse together (ट and त are both "t"), as do the
// three sibilants -- the distinction is not one a romanised query preserves.
const CONSONANTS = {
  'क': 'k', 'ख': 'kh', 'ग': 'g', 'घ': 'gh', 'ङ': 'n',
  'च': 'ch', 'छ': 'chh', 'ज': 'j', 'झ': 'jh', 'ञ': 'n',
  'ट': 't', 'ठ': 'th', 'ड': 'd', 'ढ': 'dh', 'ण': 'n',
  'त': 't', 'थ': 'th', 'द': 'd', 'ध': 'dh', 'न': 'n',
  'प': 'p', 'फ': 'ph', 'ब': 'b', 'भ': 'bh', 'म': 'm',
  'य': 'y', 'र': 'r', 'ल': 'l', 'व': 'v', 'ळ': 'l',
  'श': 's', 'ष': 's', 'स': 's', 'ह': 'h',
  'ड़': 'd', 'ढ़': 'dh', 'य़': 'y', 'ज़': 'j', 'फ़': 'ph', 'क़': 'k', 'ख़': 'kh', 'ग़': 'g',
};

// Independent vowels (word-initial position).
const VOWELS = {
  'अ': 'a', 'आ': 'a', 'इ': 'i', 'ई': 'i', 'उ': 'u', 'ऊ': 'u',
  'ए': 'e', 'ऐ': 'ai', 'ओ': 'o', 'औ': 'au',
  'ऋ': 'ri', 'ॠ': 'ri', 'ऑ': 'o', 'ऍ': 'e',
};

// Vowel signs (मात्रा) -- the same sounds written after a consonant.
const MATRAS = {
  'ा': 'a', 'ि': 'i', 'ी': 'i', 'ु': 'u', 'ू': 'u',
  'े': 'e', 'ै': 'ai', 'ो': 'o', 'ौ': 'au',
  'ृ': 'ri', 'ॉ': 'o', 'ॅ': 'e',
};

const HALANT = '्';

// Marks that carry no vowel of their own.
const SIGNS = {
  'ं': 'n',  // anusvara
  'ः': 'h',  // visarga
  'ँ': '',   // chandrabindu -- nasalisation, not a typed sound
  '़': '',   // nukta, already folded into the consonant table
  '‍': '', '‌': '',  // ZWJ / ZWNJ
};

const DIGITS = {
  '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
  '५': '5', '६': '6', '७': '7', '८': '8', '९': '9',
};

/**
 * Is this character part of a Devanagari word? Used to find word ends, which
 * is where the inherent vowel gets dropped.
 */
function isDevanagari(ch) {
  if (!ch) return false;
  return ch >= 'ऀ' && ch <= 'ॿ';
}

/**
 * Transliterate Devanagari to Latin, leaving anything else untouched.
 *
 * The subtlety is the inherent vowel: क on its own is "ka", not "k". A
 * consonant picks up an "a" unless a मात्रा supplies a different vowel or a
 * halant kills it -- except at the end of a word, where Nepali drops it.
 * स्टिल is "stil", not "stila", which is what makes it line up with someone
 * typing "steel".
 */
function transliterate(text) {
  let out = '';

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (DIGITS[ch] !== undefined) {
      out += DIGITS[ch];
      continue;
    }
    if (SIGNS[ch] !== undefined) {
      out += SIGNS[ch];
      continue;
    }
    if (VOWELS[ch] !== undefined) {
      out += VOWELS[ch];
      continue;
    }
    if (MATRAS[ch] !== undefined || ch === HALANT) {
      // Consumed by the consonant that precedes them; reaching one here means
      // it was stranded, so it contributes nothing.
      continue;
    }

    const consonant = CONSONANTS[ch];
    if (consonant === undefined) {
      out += ch;
      continue;
    }

    out += consonant;

    const next = text[i + 1];
    if (next === HALANT) {
      i++;             // dead consonant: no vowel at all
    } else if (MATRAS[next] !== undefined) {
      out += MATRAS[next];
      i++;
    } else if (isDevanagari(next)) {
      out += 'a';      // inherent vowel, mid-word
    }
    // At a word end the inherent vowel is dropped (schwa deletion).
  }

  return out;
}

// Spelling variations that romanised Nepali leaves open, applied longest-first
// so "chh" is not eaten by "ch". Everything lands on the form the Devanagari
// tables above produce.
const SPELLINGS = [
  [/aa/g, 'a'], [/ee/g, 'i'], [/ii/g, 'i'], [/oo/g, 'u'], [/uu/g, 'u'],
  [/chh/g, 'ch'],
  [/sh/g, 's'],
  [/ck/g, 'k'],
  [/f/g, 'ph'],
  [/w/g, 'v'],
  [/z/g, 'j'],
  [/x/g, 'ks'],
  [/c(?!h)/g, 'k'],   // "cooker" -> "kukar"; leaves "ch" alone
  [/y$/g, 'i'],       // trailing -y is the same sound as -i
];

/**
 * Reduce text to the form both scripts agree on.
 *
 * Spaces and punctuation go, so "1/2 kg Chini" and "आधा के.जी. चिनी" are
 * compared on their sounds rather than their formatting.
 */
export function searchKey(text) {
  if (!text || typeof text !== 'string') return '';

  let key = transliterate(text).toLowerCase();
  for (const [pattern, replacement] of SPELLINGS) {
    key = key.replace(pattern, replacement);
  }

  // Doubled consonants are not a distinction anyone types reliably.
  key = key.replace(/([a-z])\1+/g, '$1');

  return key.replace(/[^a-z0-9]/g, '');
}

/**
 * The consonant skeleton -- the key with its vowels removed.
 *
 * Vowels are where romanisation varies most ("kukar"/"kuker"/"kookar"), and
 * the consonants carry nearly all of the word's identity. This is the last
 * resort, and short queries do not get it: two consonants match far too much
 * to be useful.
 */
function skeleton(key) {
  return key.replace(/[aeiou]/g, '');
}

const MIN_SKELETON_LENGTH = 3;

// Keys are recomputed on every keystroke against the same product names, so
// they are worth holding on to. Capped because product names are user data and
// this map would otherwise grow without limit.
const MAX_CACHE = 2000;
const keyCache = new Map();

function cachedKey(text) {
  if (keyCache.has(text)) return keyCache.get(text);
  const key = searchKey(text);
  if (keyCache.size >= MAX_CACHE) keyCache.clear();
  keyCache.set(text, key);
  return key;
}

/**
 * Does any of `fields` match `query`?
 *
 * Tried in order: the text as typed, then the phonetic key, then the consonant
 * skeleton. An exact match is never lost to a phonetic one, and the loosest
 * rule only runs when the stricter ones have already failed.
 */
export function matchesQuery(query, fields) {
  const trimmed = (query || '').trim();
  if (!trimmed) return true;

  const present = fields.filter(Boolean);

  const literal = trimmed.toLowerCase();
  if (present.some((f) => f.toLowerCase().includes(literal))) return true;

  const queryKey = cachedKey(trimmed);
  if (!queryKey) return false;

  const keys = present.map(cachedKey);
  if (keys.some((k) => k.includes(queryKey))) return true;

  const queryBones = skeleton(queryKey);
  if (queryBones.length < MIN_SKELETON_LENGTH) return false;
  return keys.some((k) => skeleton(k).includes(queryBones));
}

/**
 * Match a product by name, brand, size or the code on its label. Both pages
 * search the same way, so a product found on the sale screen is also found in
 * inventory.
 *
 * Size matters here because the shop now holds several of most things: typing
 * "thali" brings back five rows and "22" is how you pick the one you want.
 * Bare numbers are included so "14" finds karai no. 14 and "1.5" finds the
 * smaller cooker.
 */
export function productMatchesQuery(product, query) {
  // The item code matches literally, never through the fuzzy path. The fuzzy
  // matcher exists to forgive romanised Nepali spellings, but its skeleton step
  // strips letters -- which made "S002" match "X002", and a clerk typing a tag
  // code at the till could tap the wrong product. Proven by doing exactly that
  // in a test: searched S002, sold a cooker.
  const q = (query || "").trim().toLowerCase();
  if (q && product.sku && product.sku.toLowerCase().includes(q)) return true;

  const fields = [product.name_en, product.name_np, product.brand];

  if (product.size_value !== null && product.size_value !== undefined) {
    fields.push(String(product.size_value));
    // "14 no" and "no 14" should both work; the shopkeeper says it either way.
    if (product.size_unit) {
      fields.push(`${product.size_value} ${product.size_unit}`);
      fields.push(`${product.size_unit} ${product.size_value}`);
    }
  }
  if (product.gauge !== null && product.gauge !== undefined) {
    fields.push(String(product.gauge));
    fields.push(`${product.gauge} gauge`);
  }

  return matchesQuery(query, fields);
}
